import React, { useState, useEffect } from 'react';
import { apiClient } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import {
  Brain, TrendingUp, Users, AlertTriangle, Sparkles, RefreshCw,
  Trophy, Target, BookOpen, ChevronRight, Clock, CheckCircle, XCircle
} from 'lucide-react';

export default function ScriptLearningPage() {
  const [data, setData] = useState({ insights: null, winning_scripts: null });
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [learning, setLearning] = useState(false);

  useEffect(() => { fetchData(); }, []);

  async function fetchData() {
    setLoading(true);
    try {
      const res = await apiClient.get('/calls/learning-insights');
      setData(res.data || {});
    } catch { /* empty state */ }
    finally { setLoading(false); }
  }

  async function runLearning() {
    setLearning(true);
    try {
      const start = await apiClient.post('/calls/learn?days=90', {});
      const job_id = start?.data?.job_id;
      if (!job_id) {
        toast.error('Could not start learning job');
        return;
      }
      toast.info('Analyzing recent calls in the background — this takes ~1 min');
      await _pollJob(job_id, 'Learning analysis complete');
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Learning failed');
    } finally { setLearning(false); }
  }

  async function generateScripts() {
    setGenerating(true);
    try {
      const start = await apiClient.post('/calls/generate-scripts', {});
      const job_id = start?.data?.job_id;
      if (!job_id) {
        toast.error('Could not start script generation');
        return;
      }
      toast.info('Generating winning scripts — this typically takes 30-60 seconds');
      await _pollJob(job_id, 'Winning scripts generated');
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Script generation failed');
    } finally { setGenerating(false); }
  }

  // Poll job status until done / failed. Long-running LLM jobs run in the
  // background so we don't hit the 30s Cloudflare edge timeout.
  async function _pollJob(job_id, successMsg) {
    const maxAttempts = 60;   // 60 × 3s = 3 min ceiling
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(r => setTimeout(r, 3000));
      try {
        const r = await apiClient.get(`/calls/jobs/${job_id}`);
        const st = r?.data?.status;
        if (st === 'done') { toast.success(successMsg); return; }
        if (st === 'failed') {
          toast.error(r?.data?.error || 'Job failed');
          return;
        }
      } catch (_) { /* transient — keep polling */ }
    }
    toast.warning('Still running — refresh in a minute to see results');
  }

  const insights = data.insights;
  const scripts = data.winning_scripts;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="script-learning-page">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="script-learning-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
            <Brain className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Script Learning Engine</h1>
            <p className="text-muted-foreground text-sm">AI learns from your calls to find what converts best</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={runLearning} disabled={learning} data-testid="run-learning-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${learning ? 'animate-spin' : ''}`} />
            {learning ? 'Analyzing...' : 'Run Learning'}
          </Button>
          <Button onClick={generateScripts} disabled={generating} data-testid="generate-scripts-btn">
            <Sparkles className={`h-4 w-4 mr-2 ${generating ? 'animate-pulse' : ''}`} />
            {generating ? 'Generating...' : 'Generate Scripts'}
          </Button>
        </div>
      </div>

      {!insights ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Brain className="h-12 w-12 mx-auto text-muted-foreground/40 mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Learning Data Yet</h3>
            <p className="text-muted-foreground text-sm mb-4">Upload and analyze call recordings first, then run learning to extract patterns.</p>
            <Button onClick={runLearning} disabled={learning}>Run First Analysis</Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card><CardContent className="pt-5 pb-4 text-center">
              <Target className="h-6 w-6 mx-auto mb-2 text-blue-500" />
              <p className="text-3xl font-bold">{insights.total_calls_analyzed}</p>
              <p className="text-xs text-muted-foreground mt-1">Calls Analyzed</p>
            </CardContent></Card>
            <Card><CardContent className="pt-5 pb-4 text-center">
              <CheckCircle className="h-6 w-6 mx-auto mb-2 text-green-500" />
              <p className="text-3xl font-bold">{insights.successful_calls}</p>
              <p className="text-xs text-muted-foreground mt-1">Successful Calls</p>
            </CardContent></Card>
            <Card><CardContent className="pt-5 pb-4 text-center">
              <XCircle className="h-6 w-6 mx-auto mb-2 text-red-500" />
              <p className="text-3xl font-bold">{insights.failed_calls}</p>
              <p className="text-xs text-muted-foreground mt-1">Failed Calls</p>
            </CardContent></Card>
            <Card><CardContent className="pt-5 pb-4 text-center">
              <TrendingUp className="h-6 w-6 mx-auto mb-2 text-emerald-500" />
              <p className="text-3xl font-bold">{insights.conversion_indicators?.avg_success_score || 0}</p>
              <p className="text-xs text-muted-foreground mt-1">Avg Winner Score</p>
            </CardContent></Card>
          </div>

          {/* Conversion Insights */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><TrendingUp className="h-4 w-4 text-emerald-500" /> Conversion Patterns</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <p className="text-sm font-medium text-green-600">Successful Calls</p>
                  <div className="flex justify-between text-sm"><span>Avg Score</span><span className="font-bold">{insights.conversion_indicators?.avg_success_score}/100</span></div>
                  <div className="flex justify-between text-sm"><span>Avg Duration</span><span className="font-bold">{Math.round((insights.conversion_indicators?.avg_success_duration || 0) / 60)}min</span></div>
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium text-red-600">Failed Calls</p>
                  <div className="flex justify-between text-sm"><span>Avg Score</span><span className="font-bold">{insights.conversion_indicators?.avg_fail_score}/100</span></div>
                  <div className="flex justify-between text-sm"><span>Avg Duration</span><span className="font-bold">{Math.round((insights.conversion_indicators?.avg_fail_duration || 0) / 60)}min</span></div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Objection Insights */}
          {insights.objection_insights?.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-orange-500" /> Objection Handling Insights</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {insights.objection_insights.map((obj, i) => (
                  <div key={i} className="p-3 rounded-lg border space-y-2" data-testid={`objection-insight-${i}`}>
                    <div className="flex items-center justify-between">
                      <Badge variant="outline">{obj.type} ({obj.total}x)</Badge>
                      <div className="flex items-center gap-2">
                        <Progress value={obj.success_rate} className="w-24" />
                        <span className={`text-sm font-bold ${obj.success_rate >= 60 ? 'text-green-600' : 'text-red-600'}`}>{obj.success_rate}%</span>
                      </div>
                    </div>
                    {obj.improvement_examples?.length > 0 && (
                      <div className="text-xs text-muted-foreground">
                        {obj.improvement_examples.map((ex, j) => (
                          <p key={j}><span className="text-red-500">Objection:</span> "{ex.objection}" <ChevronRight className="inline h-3 w-3" /> <span className="text-green-500">Better:</span> {ex.suggested}</p>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Agent Rankings */}
          {insights.agent_rankings?.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2"><Trophy className="h-4 w-4 text-yellow-500" /> Agent Rankings</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>#</TableHead><TableHead>Agent</TableHead><TableHead className="text-center">Calls</TableHead><TableHead className="text-center">Avg Score</TableHead><TableHead className="text-center">Avg Intent</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {insights.agent_rankings.map((a, i) => (
                      <TableRow key={a.agent_id} data-testid={`agent-rank-${i}`}>
                        <TableCell><Badge variant={i < 3 ? 'default' : 'outline'}>{i + 1}</Badge></TableCell>
                        <TableCell className="font-medium">{a.agent_name}</TableCell>
                        <TableCell className="text-center">{a.calls}</TableCell>
                        <TableCell className="text-center"><Badge className={a.avg_score >= 70 ? 'bg-green-500/10 text-green-600' : a.avg_score >= 40 ? 'bg-yellow-500/10 text-yellow-600' : 'bg-red-500/10 text-red-600'}>{a.avg_score}</Badge></TableCell>
                        <TableCell className="text-center">{a.avg_buying_intent}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Training Gaps */}
          {insights.training_gaps?.length > 0 && (
            <Card className="border-red-500/20">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2 text-red-600"><BookOpen className="h-4 w-4" /> Training Gaps Identified</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {insights.training_gaps.map((gap, i) => (
                  <div key={i} className="p-3 rounded-lg border border-red-500/10 bg-red-500/5">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">{gap.agent_name}</span>
                      <Badge className="bg-red-500/10 text-red-600">Score: {gap.avg_score}</Badge>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {gap.weaknesses?.map((w, j) => <Badge key={j} variant="outline" className="text-xs">{w}</Badge>)}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">{gap.recommended_training}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Winning Scripts */}
      {scripts && (
        <>
          <h2 className="text-xl font-bold flex items-center gap-2 pt-4"><Sparkles className="h-5 w-5 text-purple-500" /> AI-Generated Winning Scripts</h2>
          {scripts.scripts?.map((s, i) => (
            <Card key={i} className="border-purple-500/20" data-testid={`winning-script-${i}`}>
              <CardContent className="py-4 space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium">{s.scenario}</h3>
                  {s.conversion_boost && <Badge className="bg-purple-500/10 text-purple-600">{s.conversion_boost}</Badge>}
                </div>
                <div className="bg-purple-500/5 border border-purple-500/10 rounded-lg p-3">
                  <p className="text-sm whitespace-pre-wrap">{s.script}</p>
                </div>
                <p className="text-xs text-muted-foreground">{s.why_it_works}</p>
              </CardContent>
            </Card>
          ))}
          {scripts.closing_techniques?.length > 0 && (
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-base">Closing Techniques</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {scripts.closing_techniques.map((t, i) => (
                  <div key={i} className="p-3 rounded-lg border">
                    <p className="text-sm font-medium">{t.name}</p>
                    <p className="text-sm mt-1">{t.script}</p>
                    <p className="text-xs text-muted-foreground mt-1">Best for: {t.best_for}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
          {scripts.do_not_say?.length > 0 && (
            <Card className="border-red-500/20">
              <CardHeader className="pb-3"><CardTitle className="text-sm text-red-600">Avoid Saying</CardTitle></CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {scripts.do_not_say.map((s, i) => <Badge key={i} className="bg-red-500/10 text-red-600">{s}</Badge>)}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
